import GrossRent from './GrossRent';
import { Typography } from '@mui/material';
import AdvanceAmount from './AdvanceAmount';
import CardAccordion from '../CardAccordion';
import { constantFormValues } from '../../root';
import { useEffect, useRef, useState } from 'react';
import { withValueCap } from 'pages/globals/global';
import { NumericFormat } from 'react-number-format';
import { Col, Form, Input, Row, Select } from 'antd';
import InputLabel from 'components/InputLabel/index';
import TableContentModal from '../LeaseUtils/Modal/TableContentModal';

const FinancialTermsConditions = ({ screen, dataFullyLoaded = false }) => {
	const form = Form.useFormInstance();

	const grossRentKey = useRef('');
	const isUpdatingRef = useRef(false);
	const advanceAmountKey = useRef('');

	const grossRentList = Form.useWatch('grossRentList', form);
	const advanceAmountList = Form.useWatch('advanceAmountList', form);

	const [showGrossRentModal, setShowGrossRentModal] = useState(false);
	const [showAdvanceAmountModal, setAdvanceAmountModal] = useState(false);

	useEffect(() => {
		if (!Array.isArray(grossRentList) || !Array.isArray(advanceAmountList) || grossRentList.length === 0 || advanceAmountList.length === 0)
			return;

		const newGrossRentKey = grossRentList.map((item) => `${item.from}-${item.to}`).join('|');
		const newAdvanceAmountKey = advanceAmountList.map((item) => `${item.from}-${item.to}`).join('|');

		if (newGrossRentKey === grossRentKey.current && newAdvanceAmountKey === advanceAmountKey.current) return;
		if (isUpdatingRef.current) return;

		isUpdatingRef.current = true;

		try {
			grossRentKey.current = newGrossRentKey;
			advanceAmountKey.current = newAdvanceAmountKey;
			const maxGrossRentTerm = Math.max(...grossRentList.map((item) => item.to ?? 0));
			const maxAdvanceAmountTerm = Math.max(...advanceAmountList.map((item) => item.to ?? 0));
			const maxTerm = Math.max(maxGrossRentTerm, maxAdvanceAmountTerm);
			const currentLeaseTerm = form.getFieldValue('leaseTerm');

			if (currentLeaseTerm !== maxTerm) {
				form.setFieldValue('leaseTerm', maxTerm);

				if (screen?.toLowerCase() === 'revise') {
					const currentChanges = form.getFieldValue('changes');

					if (currentChanges?.length > 0) {
						form.setFieldValue(['changes', currentChanges.length - 1, 'inputChangeLeaseTerm'], maxTerm);
					}
				}

				if (screen?.toLowerCase() === 'create') {
					const currentChangeLeaseTerm = form.getFieldValue(['changes', 0, 'inputChangeLeaseTerm']);

					if (currentChangeLeaseTerm !== maxTerm) {
						form.setFieldValue(['changes', 0, 'inputChangeLeaseTerm'], maxTerm);
					}
				}
			}

			const leaseStartDate = form.getFieldValue('leaseStartDate');

			if (leaseStartDate && maxTerm) {
				const leaseEndDate = leaseStartDate.add(maxTerm, 'month').subtract(1, 'day');
				const currentLeaseEndDate = form.getFieldValue('leaseEndDate');

				if (!currentLeaseEndDate || !leaseEndDate.isSame(currentLeaseEndDate)) {
					form.setFieldValue('leaseEndDate', leaseEndDate);
				}
			}
		} finally {
			setTimeout(() => {
				isUpdatingRef.current = false;
			}, 0);
		}
	}, [grossRentList, advanceAmountList, screen]);

	const handlePaymentFrequencyChange = (value) => {
		if (isUpdatingRef.current) return value;

		isUpdatingRef.current = true;

		try {
			const currentChanges = form.getFieldValue('changes');

			if (screen.toLowerCase() === 'create') {
				form.setFieldValue(['changes', 0, 'paymentFrequency'], value);
			} else if (screen.toLowerCase() === 'revise') {
				form.setFieldValue(['changes', currentChanges.length - 1, 'paymentFrequency'], value);
			}
		} finally {
			isUpdatingRef.current = false;
		}

		return value;
	};

	const handlePaymentFrequencyBlur = () => {
		if (screen === 'EDIT' && window.debouncedManageChangeRows) {
			window.debouncedManageChangeRows();
		}
	};

	const handlePaymentModalityChange = (value) => {
		if (isUpdatingRef.current) return value;

		isUpdatingRef.current = true;

		try {
			const currentChanges = form.getFieldValue('changes');

			if (screen.toLowerCase() === 'create') {
				form.setFieldValue(['changes', 0, 'paymentTime'], value);
			} else if (screen.toLowerCase() === 'revise') {
				form.setFieldValue(['changes', currentChanges.length - 1, 'paymentTime'], value);
			}
		} finally {
			isUpdatingRef.current = false;
		}
		return value;
	};

	const handlePaymentModalityBlur = () => {
		if (screen === 'EDIT' && window.debouncedManageChangeRows) {
			window.debouncedManageChangeRows();
		}
	};

	const grossRentModalData = { head: 'Monthly Gross Rent Schedule', label: 'Define monthly gross rent values', action: 'update' };
	const advanceAmountModalData = { head: 'Advance Amount Schedule', label: 'Define advance amount and adjustment values', action: 'update' };

	return (
		<>
			<Row gutter={[16, 0]}>
				<Col xs={24} md={12}>
					<Typography
						variant="h4"
						sx={{
							fontWeight: 600,
							fontSize: '18px',
							color: '#1a3b5d',
							marginBottom: '16px',
							padding: '0 0 12px 0',
							borderBottom: '1px solid #e0e6ed',
						}}
					>
						Lease Payment Schedule
					</Typography>

					<GrossRent formListName="grossRentList" screen={screen} />

					<Form.Item name="identityId" noStyle />
					<Form.Item name="grossRentListSet" noStyle />
					<Form.Item name="effectiveDateHistory" noStyle />
					<Form.Item name="modificationSequence" noStyle />

					<Row gutter={[16, 0]} style={{ marginTop: '16px' }}>
						<Col xs={24} md={12}>
							<Form.Item
								name={constantFormValues?.paymentFrequency?.key}
								getValueFromEvent={handlePaymentFrequencyChange}
								rules={constantFormValues?.paymentFrequency?.rules}
								label={constantFormValues?.paymentFrequency?.label}
							>
								<Select
									showSearch
									filterOption={true}
									optionFilterProp="children"
									disabled={screen === 'VIEW'}
									placeholder="Select from the list"
									onBlur={handlePaymentFrequencyBlur}
									style={{ width: '100%', height: '32px', borderRadius: '4px' }}
								>
									{constantFormValues?.paymentFrequency?.options?.map(({ key, value, label }) => (
										<Select.Option key={key} value={value}>
											{label}
										</Select.Option>
									))}
								</Select>
							</Form.Item>
						</Col>

						<Col xs={24} md={12}>
							<Form.Item
								getValueFromEvent={handlePaymentModalityChange}
								name={constantFormValues?.paymentModality?.key}
								rules={constantFormValues?.paymentModality?.rules}
								label={constantFormValues?.paymentModality?.label}
							>
								<Select
									showSearch
									filterOption={true}
									optionFilterProp="children"
									disabled={screen === 'VIEW'}
									placeholder="Select from the list"
									onBlur={handlePaymentModalityBlur}
									style={{ width: '100%', height: '32px', borderRadius: '4px' }}
								>
									{constantFormValues?.paymentModality?.options?.map(({ key, value, label }) => (
										<Select.Option key={key} value={value}>
											{label}
										</Select.Option>
									))}
								</Select>
							</Form.Item>
						</Col>
					</Row>
				</Col>

				{screen !== 'EDIT' && (
					<Col xs={24} md={12}>
						<Typography
							variant="h4"
							sx={{
								fontWeight: 600,
								fontSize: '18px',
								color: '#1a3b5d',
								marginBottom: '16px',
								padding: '0 0 12px 0',
								borderBottom: '1px solid #e0e6ed',
							}}
						>
							Advance Payment & Adjustment
						</Typography>

						<AdvanceAmount formListName="advanceAmountList" screen={screen} dataFullyLoaded={dataFullyLoaded} />

						<Form.Item name="advanceAmountListSet" noStyle />
					</Col>
				)}

				{screen !== 'EDIT' && (
					<Col xs={24}>
						<Typography
							variant="h4"
							sx={{
								fontWeight: 600,
								fontSize: '18px',
								color: '#1a3b5d',
								marginBottom: '16px',
								padding: '0 0 12px 0',
								borderBottom: '1px solid #e0e6ed',
							}}
						>
							Other Payments & Incentive
						</Typography>

						<Row gutter={[16, 0]}>
							<Col xs={24} md={6}>
								<Form.Item
									name={constantFormValues?.upfrontLeasePayment?.key}
									rules={constantFormValues?.upfrontLeasePayment?.rules}
									label={constantFormValues?.upfrontLeasePayment?.label}
								>
									<NumericFormat
										size="large"
										decimalScale={2}
										fixedDecimalScale
										placeholder="0.00"
										customInput={Input}
										thousandSeparator=","
										isAllowed={withValueCap}
										disabled={screen === 'VIEW'}
										style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
									/>
								</Form.Item>
							</Col>

							<Col xs={24} md={6}>
								<Form.Item
									name={constantFormValues?.initialDirectCost?.key}
									rules={constantFormValues?.initialDirectCost?.rules}
									label={constantFormValues?.initialDirectCost?.label}
								>
									<NumericFormat
										size="large"
										decimalScale={2}
										placeholder="0.00"
										fixedDecimalScale
										customInput={Input}
										thousandSeparator=","
										isAllowed={withValueCap}
										disabled={screen === 'VIEW'}
										style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
									/>
								</Form.Item>
							</Col>

							<Col xs={24} md={6}>
								<Form.Item
									name={constantFormValues?.securityDeposit?.key}
									rules={constantFormValues?.securityDeposit?.rules}
									label={constantFormValues?.securityDeposit?.label}
								>
									<NumericFormat
										size="large"
										decimalScale={2}
										fixedDecimalScale
										placeholder="0.00"
										customInput={Input}
										thousandSeparator=","
										isAllowed={withValueCap}
										disabled={screen === 'VIEW'}
										style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
									/>
								</Form.Item>
							</Col>

							<Col xs={24} md={6}>
								<Form.Item
									rules={constantFormValues?.securityDeposit?.rules}
									name={constantFormValues?.cashIncentiveAmount?.key}
									label={constantFormValues?.cashIncentiveAmount?.label}
								>
									<NumericFormat
										size="large"
										decimalScale={2}
										placeholder="0.00"
										fixedDecimalScale
										customInput={Input}
										thousandSeparator=","
										isAllowed={withValueCap}
										disabled={screen === 'VIEW'}
										style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
									/>
								</Form.Item>
							</Col>
						</Row>
					</Col>
				)}

				<Form.Item name="grossRents" noStyle />
				<Form.Item name="advanceAmounts" noStyle />
				<Form.Item name={constantFormValues?.advanceAmount?.key} noStyle />
				<Form.Item noStyle name={constantFormValues?.inputChangeMonth?.key} />
			</Row>

			{showGrossRentModal && screen !== 'VIEW' && (
				<TableContentModal
					form={form}
					numberOfColumn={3}
					modalData={grossRentModalData}
					showModal={showGrossRentModal}
					formListName="grossRentSchedule"
					setShowModal={setShowGrossRentModal}
				/>
			)}

			{showAdvanceAmountModal && screen !== 'VIEW' && (
				<TableContentModal
					form={form}
					numberOfColumn={4}
					showModal={showAdvanceAmountModal}
					setShowModal={setAdvanceAmountModal}
					formListName="advanceAmountSchedule"
					modalData={advanceAmountModalData}
				/>
			)}
		</>
	);
};

export default FinancialTermsConditions;

// import React, { useEffect, useRef, useState } from 'react';
// import { Col, Form, Input, Row, Select } from 'antd';
// import InputLabel from 'components/InputLabel/index';
// import { NumericFormat } from 'react-number-format';
// import { withValueCap } from 'pages/globals/global';
// import TableContentModal from '../LeaseUtils/Modal/TableContentModal';
// import CardAccordion from '../CardAccordion';
// import GrossRent from './GrossRent';
// import AdvanceAmount from './AdvanceAmount';
// import { constantFormValues } from '../../root';

// const FinancialTermsConditions = ({ screen, dataFullyLoaded = false }) => {
// 	const form = Form.useFormInstance();
// 	const [showGrossRentModal, setShowGrossRentModal] = useState(false);
// 	const [showAdvanceAmountModal, setAdvanceAmountModal] = useState(false);
// 	const isUpdatingRef = useRef(false);
// 	const grossRentList = Form.useWatch('grossRentList', form);
// 	const advanceAmountList = Form.useWatch('advanceAmountList', form);
// 	const grossRentKey = useRef('');
// 	const advanceAmountKey = useRef('');

// 	// --- No changes to any of the logic (useEffect, handlers, etc.) ---
// 	useEffect(() => {
// 		if (!Array.isArray(grossRentList) || !Array.isArray(advanceAmountList) || grossRentList.length === 0 || advanceAmountList.length === 0)
// 			return;
// 		const newGrossRentKey = grossRentList.map((item) => `${item.from}-${item.to}`).join('|');
// 		const newAdvanceAmountKey = advanceAmountList.map((item) => `${item.from}-${item.to}`).join('|');
// 		if (newGrossRentKey === grossRentKey.current && newAdvanceAmountKey === advanceAmountKey.current) return;
// 		if (isUpdatingRef.current) return;
// 		isUpdatingRef.current = true;
// 		try {
// 			grossRentKey.current = newGrossRentKey;
// 			advanceAmountKey.current = newAdvanceAmountKey;
// 			const maxGrossRentTerm = Math.max(...grossRentList.map((item) => item.to ?? 0));
// 			const maxAdvanceAmountTerm = Math.max(...advanceAmountList.map((item) => item.to ?? 0));
// 			const maxTerm = Math.max(maxGrossRentTerm, maxAdvanceAmountTerm);
// 			const currentLeaseTerm = form.getFieldValue('leaseTerm');
// 			if (currentLeaseTerm !== maxTerm) {
// 				form.setFieldValue('leaseTerm', maxTerm);
// 				if (screen?.toLowerCase() === 'revise') {
// 					const currentChanges = form.getFieldValue('changes');
// 					if (currentChanges?.length > 0) {
// 						form.setFieldValue(['changes', currentChanges.length - 1, 'inputChangeLeaseTerm'], maxTerm);
// 					}
// 				}
// 				if (screen?.toLowerCase() === 'create') {
// 					const currentChangeLeaseTerm = form.getFieldValue(['changes', 0, 'inputChangeLeaseTerm']);
// 					if (currentChangeLeaseTerm !== maxTerm) {
// 						form.setFieldValue(['changes', 0, 'inputChangeLeaseTerm'], maxTerm);
// 					}
// 				}
// 			}
// 			const leaseStartDate = form.getFieldValue('leaseStartDate');
// 			if (leaseStartDate && maxTerm) {
// 				const leaseEndDate = leaseStartDate.add(maxTerm, 'month').subtract(1, 'day');
// 				const currentLeaseEndDate = form.getFieldValue('leaseEndDate');
// 				if (!currentLeaseEndDate || !leaseEndDate.isSame(currentLeaseEndDate)) {
// 					form.setFieldValue('leaseEndDate', leaseEndDate);
// 				}
// 			}
// 		} finally {
// 			setTimeout(() => {
// 				isUpdatingRef.current = false;
// 			}, 0);
// 		}
// 	}, [grossRentList, advanceAmountList, screen]);

// 	const handlePaymentFrequencyChange = (value) => {
// 		if (isUpdatingRef.current) return value;
// 		isUpdatingRef.current = true;
// 		try {
// 			const currentChanges = form.getFieldValue('changes');
// 			if (screen.toLowerCase() === 'create') {
// 				form.setFieldValue(['changes', 0, 'paymentFrequency'], value);
// 			} else if (screen.toLowerCase() === 'revise') {
// 				form.setFieldValue(['changes', currentChanges.length - 1, 'paymentFrequency'], value);
// 			}
// 		} finally {
// 			isUpdatingRef.current = false;
// 		}
// 		return value;
// 	};
// 	const handlePaymentFrequencyBlur = () => {
// 		if (screen === 'EDIT' && window.debouncedManageChangeRows) {
// 			window.debouncedManageChangeRows();
// 		}
// 	};
// 	const handlePaymentModalityChange = (value) => {
// 		if (isUpdatingRef.current) return value;
// 		isUpdatingRef.current = true;
// 		try {
// 			const currentChanges = form.getFieldValue('changes');
// 			if (screen.toLowerCase() === 'create') {
// 				form.setFieldValue(['changes', 0, 'paymentTime'], value);
// 			} else if (screen.toLowerCase() === 'revise') {
// 				form.setFieldValue(['changes', currentChanges.length - 1, 'paymentTime'], value);
// 			}
// 		} finally {
// 			isUpdatingRef.current = false;
// 		}
// 		return value;
// 	};
// 	const handlePaymentModalityBlur = () => {
// 		if (screen === 'EDIT' && window.debouncedManageChangeRows) {
// 			window.debouncedManageChangeRows();
// 		}
// 	};

// 	const grossRentModalData = { head: 'Monthly Gross Rent Schedule', label: 'Define monthly gross rent values', action: 'update' };
// 	const advanceAmountModalData = { head: 'Advance Amount Schedule', label: 'Define advance amount and adjustment values', action: 'update' };
// 	// --- End of logic section ---

// 	return (
// 		<>
// 			<Row gutter={[16, 0]}>
// 				<Col xs={24} md={12}>
// 					<CardAccordion
// 						title="Lease Payment Schedule"
// 						customHeaderSX={{ backgroundColor: '#e1f0fb !important', p: '10px', mb: 3 }}
// 						typographySX={{ fontSize: '16px !important' }}
// 						darkTitle={false}
// 						sx={{ mb: 2 }}
// 					>

// 						<GrossRent formListName="grossRentList" screen={screen} />
// 						<Form.Item name="grossRentListSet" noStyle />
// 						<Form.Item name="effectiveDateHistory" noStyle />
// 						<Form.Item name="identityId" noStyle />
// 						<Form.Item name="modificationSequence" noStyle />
// 					</CardAccordion>

// 					<Row gutter={[16, 0]}>
// 						{/* --- Payment Frequency --- */}
// 						{/* RESPONSIVE CHANGE: Label stacks on mobile */}
// 						<Col xs={24} md={6}>
// 							<InputLabel label={constantFormValues?.paymentFrequency?.label} head={true} />
// 						</Col>
// 						{/* RESPONSIVE CHANGE: Input stacks on mobile */}
// 						<Col xs={24} md={6}>
// 							<Form.Item
// 								name={constantFormValues?.paymentFrequency?.key}
// 								rules={constantFormValues?.paymentFrequency?.rules}
// 								getValueFromEvent={handlePaymentFrequencyChange}
// 							>
// 								<Select
// 									disabled={screen === 'VIEW'}
// 									showSearch
// 									style={{ width: '100%', height: '32px', borderRadius: '4px' }}
// 									filterOption={true}
// 									placeholder="Select from the list"
// 									optionFilterProp="children"
// 									onBlur={handlePaymentFrequencyBlur}
// 								>
// 									{constantFormValues?.paymentFrequency?.options?.map(({ key, value, label }) => (
// 										<Select.Option key={key} value={value}>
// 											{label}
// 										</Select.Option>
// 									))}
// 								</Select>
// 							</Form.Item>
// 						</Col>

// 						{/* --- Payment Modality --- */}
// 						<Col xs={24} md={6}>
// 							<InputLabel label={constantFormValues?.paymentModality?.label} head={true} />
// 						</Col>
// 						<Col xs={24} md={6}>
// 							<Form.Item
// 								name={constantFormValues?.paymentModality?.key}
// 								rules={constantFormValues?.paymentModality?.rules}
// 								getValueFromEvent={handlePaymentModalityChange}
// 							>
// 								<Select
// 									disabled={screen === 'VIEW'}
// 									showSearch
// 									style={{ width: '100%', height: '32px', borderRadius: '4px' }}
// 									filterOption={true}
// 									placeholder="Select from the list"
// 									optionFilterProp="children"
// 									onBlur={handlePaymentModalityBlur}
// 								>
// 									{constantFormValues?.paymentModality?.options?.map(({ key, value, label }) => (
// 										<Select.Option key={key} value={value}>
// 											{label}
// 										</Select.Option>
// 									))}
// 								</Select>
// 							</Form.Item>
// 						</Col>
// 					</Row>
// 				</Col>

// 				{screen !== 'EDIT' && (
// 					<Col xs={24} md={12}>
// 						<CardAccordion
// 							title="Advance Payment & Adjustment"
// 							customHeaderSX={{ backgroundColor: '#e1f0fb !important', p: '10px', mb: 3 }}
// 							typographySX={{ fontSize: '16px !important' }}
// 							darkTitle={false}
// 							sx={{ mb: 2 }}
// 						>
// 							<AdvanceAmount formListName="advanceAmountList" screen={screen} dataFullyLoaded={dataFullyLoaded} />
// 							<Form.Item name="advanceAmountListSet" noStyle />
// 						</CardAccordion>
// 					</Col>
// 				)}

// 				{screen !== 'EDIT' && (
// 					<Col xs={24}>
// 						<CardAccordion
// 							title="Other Payments & Incentive"
// 							customHeaderSX={{ backgroundColor: '#e1f0fb !important', p: '10px' }}
// 							typographySX={{ fontSize: '16px !important' }}
// 							darkTitle={false}
// 						>
// 							{/* RESPONSIVE CHANGE: Added vertical gutter to the inner row */}
// 							<Row gutter={[16, 0]}>
// 								<Col xs={24} md={4}>
// 									<InputLabel label={constantFormValues?.upfrontLeasePayment?.label} head={true} />
// 								</Col>
// 								<Col xs={24} md={8}>
// 									<Form.Item
// 										name={constantFormValues?.upfrontLeasePayment?.key}
// 										rules={constantFormValues?.upfrontLeasePayment?.rules}
// 									>
// 										<NumericFormat
// 											size="large"
// 											decimalScale={2}
// 											placeholder="0.00"
// 											fixedDecimalScale
// 											thousandSeparator=","
// 											isAllowed={withValueCap}
// 											style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
// 											customInput={Input}
// 											disabled={screen === 'VIEW'}
// 										/>
// 									</Form.Item>
// 								</Col>

// 								<Col xs={24} md={4}>
// 									<InputLabel label={constantFormValues?.initialDirectCost?.label} head={true} />
// 								</Col>
// 								<Col xs={24} md={8}>
// 									<Form.Item name={constantFormValues?.initialDirectCost?.key} rules={constantFormValues?.initialDirectCost?.rules}>
// 										<NumericFormat
// 											size="large"
// 											decimalScale={2}
// 											placeholder="0.00"
// 											fixedDecimalScale
// 											thousandSeparator=","
// 											isAllowed={withValueCap}
// 											style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
// 											customInput={Input}
// 											disabled={screen === 'VIEW'}
// 										/>
// 									</Form.Item>
// 								</Col>

// 								<Col xs={24} md={4}>
// 									<InputLabel label={constantFormValues?.securityDeposit?.label} head={true} />
// 								</Col>
// 								<Col xs={24} md={8}>
// 									<Form.Item name={constantFormValues?.securityDeposit?.key} rules={constantFormValues?.securityDeposit?.rules}>
// 										<NumericFormat
// 											size="large"
// 											decimalScale={2}
// 											placeholder="0.00"
// 											fixedDecimalScale
// 											thousandSeparator=","
// 											isAllowed={withValueCap}
// 											style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
// 											customInput={Input}
// 											disabled={screen === 'VIEW'}
// 										/>
// 									</Form.Item>
// 								</Col>
// 								<Col xs={24} md={4}>
// 									<InputLabel label={constantFormValues?.cashIncentiveAmount?.label} head={true} />
// 								</Col>
// 								<Col xs={24} md={8}>
// 									<Form.Item name={constantFormValues?.cashIncentiveAmount?.key} rules={constantFormValues?.securityDeposit?.rules}>
// 										<NumericFormat
// 											size="large"
// 											decimalScale={2}
// 											placeholder="0.00"
// 											fixedDecimalScale
// 											thousandSeparator=","
// 											isAllowed={withValueCap}
// 											style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
// 											customInput={Input}
// 											disabled={screen === 'VIEW'}
// 										/>
// 									</Form.Item>
// 								</Col>
// 							</Row>
// 						</CardAccordion>
// 					</Col>
// 				)}

// 				{/* Hidden form items remain unchanged */}
// 				<Form.Item name={constantFormValues?.advanceAmount?.key} noStyle />
// 				<Form.Item name="advanceAmounts" noStyle />
// 				<Form.Item name="grossRents" noStyle />
// 				<Form.Item noStyle name={constantFormValues?.inputChangeMonth?.key} />
// 			</Row>

// 			{/* Modal logic remains unchanged */}
// 			{showGrossRentModal && screen !== 'VIEW' && (
// 				<TableContentModal
// 					form={form}
// 					showModal={showGrossRentModal}
// 					setShowModal={setShowGrossRentModal}
// 					formListName="grossRentSchedule"
// 					modalData={grossRentModalData}
// 					numberOfColumn={3}
// 				/>
// 			)}

// 			{showAdvanceAmountModal && screen !== 'VIEW' && (
// 				<TableContentModal
// 					form={form}
// 					showModal={showAdvanceAmountModal}
// 					setShowModal={setAdvanceAmountModal}
// 					formListName="advanceAmountSchedule"
// 					modalData={advanceAmountModalData}
// 					numberOfColumn={4}
// 				/>
// 			)}
// 		</>
// 	);
// };

// export default FinancialTermsConditions;
